import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useVehicle } from "../../contexts/VehicleContext";
import { FaTimes, FaFilter, FaSpinner } from "react-icons/fa";
import { vehicleCatalogService } from "../../services/apiService";

const CatalogueSidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const { selectedVehicle } = useVehicle();
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const location = useLocation();

  // Prefer URL params; fall back to selectedVehicle from context
  const urlParams = new URLSearchParams(location.search);
  const vehicleMake = urlParams.get('vehicleMake') || selectedVehicle?.make || '';
  const vehicleModel = urlParams.get('vehicleModel') || selectedVehicle?.model || '';
  const vehicleYear = urlParams.get('vehicleYear') || selectedVehicle?.year || '';
  const rawVariant = urlParams.get('vehicleVariant') || selectedVehicle?.variant || '';
  const vehicleVariant = (() => {
    if (!rawVariant || rawVariant === 'All' || !rawVariant.includes('/')) return rawVariant;
    const trimPart = rawVariant.split('/')[0].trim();
    return trimPart.replace(/^\d+[\d.]*[Lcc]+\s*/i, '').trim() || trimPart;
  })();
  const vehicleFuelType = urlParams.get('vehicleFuelType') || selectedVehicle?.fuelType || '';
  const catalogModelId =
    urlParams.get("catalogModelId") ||
    selectedVehicle?.modelId ||
    selectedVehicle?.catalogModelId ||
    "";
  const [resolvedCatalogModelId, setResolvedCatalogModelId] = useState("");
  const spareType = urlParams.get('spareType') || '';
  const origin = urlParams.get('origin') || '';
  const selectedCategory = urlParams.get('category') || '';
  const pathnameCategory = location.pathname.startsWith("/catalog/")
    ? location.pathname.replace(/^\/catalog\//, "").replace(/\/+$/, "")
    : "";
  const activeCategorySlug = (selectedCategory || pathnameCategory || "").toLowerCase();

  // Auto-expand Maintenance Service Parts if on Belt or Timing Belt page
  const isBeltPage = location.pathname === "/catalog/4032-belts/";
  const isTimingBeltPage = location.pathname === "/catalog/4390-timing_belt/" || location.pathname === "/catalog/4033-time_belt/";
  const [expandedCategories, setExpandedCategories] = useState({
    "Maintenance Service Parts": isBeltPage || isTimingBeltPage ? true : false
  });

  const normalizeCategoryKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[\s/_-]+/g, " ");

  // Resolve model id from make/model when query/context doesn't include catalogModelId.
  useEffect(() => {
    let cancelled = false;
    const resolveModelId = async () => {
      if (catalogModelId) {
        setResolvedCatalogModelId(String(catalogModelId));
        return;
      }
      if (!vehicleMake || !vehicleModel) {
        setResolvedCatalogModelId("");
        return;
      }
      try {
        const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
        const maker = (Array.isArray(makers) ? makers : []).find(
          (m) => String(m?.name || "").trim().toLowerCase() === String(vehicleMake).trim().toLowerCase()
        );
        const makerId = maker?._id || maker?.id;
        if (!makerId) {
          if (!cancelled) setResolvedCatalogModelId("");
          return;
        }
        const models = await vehicleCatalogService.listModelsPublic(makerId, { includeInactive: true });
        const rawModel = String(vehicleModel || "").trim().toLowerCase();
        const model = (Array.isArray(models) ? models : []).find((m) => {
          const name = String(m?.name || "").trim().toLowerCase();
          return name === rawModel || name.endsWith(` ${rawModel}`);
        });
        if (!cancelled) setResolvedCatalogModelId(String(model?._id || model?.id || ""));
      } catch {
        if (!cancelled) setResolvedCatalogModelId("");
      }
    };
    resolveModelId();
    return () => {
      cancelled = true;
    };
  }, [catalogModelId, vehicleMake, vehicleModel]);

  // Fetch categories only from Vehicle Management catalogue
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        if (!resolvedCatalogModelId) {
          setCategories([]);
          return;
        }
        const modelCategories = await vehicleCatalogService.listCategoriesPublic(resolvedCatalogModelId);
        const vcCategories = Array.isArray(modelCategories) ? modelCategories : [];

        const withParts = await Promise.all(
          vcCategories.map(async (cat) => {
            const categoryId = cat?._id || cat?.id;
            if (!categoryId) return null;
            try {
              const parts = await vehicleCatalogService.listPartsPublic(categoryId, { modelId: resolvedCatalogModelId });
              return Array.isArray(parts) && parts.length > 0 ? cat : null;
            } catch {
              return null;
            }
          })
        );

        const visible = withParts
          .filter(Boolean)
          .map((cat) => {
            const catName = String(cat?.name || "").trim();
            if (!catName) return null;
            const slug =
              String(cat?.slug || "").trim() ||
              catName.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]+/g, "");
            return {
              id: String(cat?._id || cat?.id || ""),
              name: catName,
              link: `/catalog/${slug}/`,
              parentId: null,
              subcategoryCount: 0,
              subCategories: []
            };
          })
          .filter(Boolean);

        setCategories(visible);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [resolvedCatalogModelId, vehicleMake, vehicleModel, vehicleYear, vehicleVariant, vehicleFuelType, spareType, origin, selectedVehicle?.modelId, selectedVehicle?.catalogModelId]);

  // Categories are fetched from API (see useEffect above)

  const toggleCategory = (categoryName, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedCategories(prev => {
      const newState = { ...prev };
      // Close all other categories
      Object.keys(newState).forEach(key => {
        if (key !== categoryName) {
          newState[key] = false;
        }
      });
      // Toggle only the clicked category
      newState[categoryName] = !prev[categoryName];
      return newState;
    });
  };

  const handleReset = () => {
    setExpandedCategories({});
  };

  const categoryLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (slug) => {
      const safeSlug = (slug || "").toString().replace(/^\/+|\/+$/g, "");
      // Keep vehicle/tier filters in query params, but avoid redirecting to /catalog Step-1 page.
      params.delete("category");
      const query = params.toString();
      return `/catalog/${safeSlug}/${query ? `?${query}` : ""}`;
    };
  }, [location.search]);

  // Fetch brands from Vehicle Management parts only
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        if (!resolvedCatalogModelId) {
          setBrands([]);
          return;
        }
        const modelCategories = await vehicleCatalogService.listCategoriesPublic(resolvedCatalogModelId);
        const vcCategories = Array.isArray(modelCategories) ? modelCategories : [];
        const selectedCategoryNorm = normalizeCategoryKey(selectedCategory.replace(/_/g, " "));

        const categoriesToRead = selectedCategory
          ? vcCategories.filter((c) => {
              const byName = normalizeCategoryKey(c?.name);
              const bySlug = normalizeCategoryKey(c?.slug);
              return byName === selectedCategoryNorm || bySlug === selectedCategoryNorm;
            })
          : vcCategories;

        const partsByCategory = await Promise.all(
          categoriesToRead.map(async (cat) => {
            const categoryId = cat?._id || cat?.id;
            if (!categoryId) return [];
            try {
              const parts = await vehicleCatalogService.listPartsPublic(categoryId, { modelId: resolvedCatalogModelId });
              return Array.isArray(parts) ? parts : [];
            } catch {
              return [];
            }
          })
        );
        const productList = partsByCategory.flat();

        const filteredByOrigin = origin
          ? productList.filter(
              (p) =>
                String(p?.origin || "")
                  .trim()
                  .toLowerCase() === String(origin).toLowerCase()
            )
          : productList;

        const uniqueBrandNames = [...new Set(
          filteredByOrigin
            .map((item) => item?.brand || item?.manufacturer)
            .filter((name) => typeof name === "string" && name.trim().length > 0)
            .map((name) => name.trim())
        )].sort((a, b) => a.localeCompare(b));

        if (!uniqueBrandNames.length) {
          // Vehicle catalogue parts often omit brand. Show a safe placeholder.
          setBrands(["Vehicle Catalogue"]);
          return;
        }

        setBrands(uniqueBrandNames);
      } catch (error) {
        console.error("Failed to fetch brands:", error);
        setBrands([]);
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, [resolvedCatalogModelId, vehicleMake, vehicleModel, vehicleYear, vehicleVariant, vehicleFuelType, spareType, origin, selectedCategory]);

  const sidebarContent = (
    <div className="bg-white dark:bg-gray-900 p-3 sm:p-4 md:p-5 rounded-lg sm:rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-4 max-h-[calc(100vh-2rem)] overflow-y-auto scrollbar-hide">

        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200">
          <h3 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-semibold text-gray-800 dark:text-gray-100">Filters</h3>
          <button 
            onClick={handleReset}
            className="text-[9px] sm:text-[10px] md:text-xs text-[#131c36] hover:text-[#0f1528] dark:text-[#131c36] font-medium"
          >
            RESET
          </button>
        </div>

        {/* Category Section */}
        <div className="mb-6 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-gray-300 mb-3">
            Category
          </h4>
          {loadingCategories ? (
            <div className="text-center py-4">
              <FaSpinner className="animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-gray-500">No categories available</p>
              <p className="text-[10px] text-gray-400 mt-1">Please add categories from admin panel</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {categories.map((category) => (
              <div key={category.name}>
                <div className="flex items-center justify-between">
                  <Link
                    to={categoryLink(category.link.split("/catalog/")[1]?.replaceAll("/", "") || category.name.toLowerCase().replace(/\s+/g, "_"))}
                    className={`flex-1 block text-xs px-2 py-1.5 rounded transition-all duration-200 ${
                      activeCategorySlug === (category.link.split("/catalog/")[1]?.replaceAll("/", "") || category.name.toLowerCase().replace(/\s+/g, "_")).toLowerCase()
                        ? "font-semibold text-white bg-[#131c36]"
                        : "text-gray-700 dark:text-gray-300 hover:text-[#131c36] hover:bg-[#131c36]/10"
                    }`}
                  >
                    {category.name}
                  </Link>
                  {category.subCategories && category.subCategories.length > 0 && (
                    <button
                      onClick={(e) => toggleCategory(category.name, e)}
                      className="text-gray-500 hover:text-gray-700 px-1 text-xs"
                      aria-label={expandedCategories[category.name] ? "Collapse" : "Expand"}
                    >
                      {expandedCategories[category.name] ? "−" : "+"}
                    </button>
                  )}
                </div>
                {category.subCategories && category.subCategories.length > 0 && expandedCategories[category.name] && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                    {category.subCategories.map((subCat) => (
                      <div key={subCat.name || subCat.link}>
                        {subCat.subItems ? (
                          // Nested sub-category (e.g., Belt with sub-items)
                          <div>
                            <Link
                              to={categoryLink(subCat.link.split("/catalog/")[1]?.replaceAll("/", "") || subCat.name.toLowerCase().replace(/\s+/g, "_"))}
                              className={`block text-[10px] font-medium px-2 py-1 mb-0.5 rounded transition-all duration-200 ${
                                activeCategorySlug === (subCat.link.split("/catalog/")[1]?.replaceAll("/", "") || subCat.name.toLowerCase().replace(/\s+/g, "_")).toLowerCase()
                                  ? "text-[#131c36] bg-[#131c36]/10"
                                  : "text-gray-700 hover:text-[#131c36] hover:bg-gray-50"
                              }`}
                            >
                              {subCat.name}
                            </Link>
                            <div className="ml-2 space-y-0.5">
                              {subCat.subItems.map((item) => (
                                <Link
                                  key={item.name}
                                  to={categoryLink(item.link.split("/catalog/")[1]?.replaceAll("/", "") || item.name.toLowerCase().replace(/\s+/g, "_"))}
                                  className={`block text-[10px] px-2 py-0.5 rounded transition-all duration-200 ${
                                    activeCategorySlug === (item.link.split("/catalog/")[1]?.replaceAll("/", "") || item.name.toLowerCase().replace(/\s+/g, "_")).toLowerCase()
                                      ? "font-medium text-[#131c36] bg-[#131c36]/10"
                                      : "text-gray-600 hover:text-[#131c36] hover:bg-gray-50"
                                  }`}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // Regular sub-category
                          <Link
                            to={categoryLink(subCat.link.split("/catalog/")[1]?.replaceAll("/", "") || subCat.name.toLowerCase().replace(/\s+/g, "_"))}
                            className={`block text-[10px] px-2 py-1 rounded transition-all duration-200 ${
                              activeCategorySlug === (subCat.link.split("/catalog/")[1]?.replaceAll("/", "") || subCat.name.toLowerCase().replace(/\s+/g, "_")).toLowerCase()
                                ? "font-medium text-[#131c36] bg-[#131c36]/10"
                                : "text-gray-600 hover:text-[#131c36] hover:bg-gray-50"
                            }`}
                          >
                            {subCat.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              ))}
            </div>
          )}
        </div>

        {/* Brand Section */}
        <div className="mb-6">
          <h4 className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-gray-300 mb-3">Brand</h4>
          {loadingBrands ? (
            <div className="text-center py-2">
              <FaSpinner className="animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Loading brands...</p>
            </div>
          ) : brands.length === 0 ? (
            <p className="text-xs text-gray-500">No brands available</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {brands.map((brandName) => (
                <div
                  key={brandName}
                  className="block text-xs px-2 py-1.5 rounded text-gray-700 dark:text-gray-300 hover:text-[#131c36] hover:bg-[#131c36]/10 dark:hover:bg-gray-700"
                >
                  {brandName}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  );

  return (
    <>
      {/* Mobile Filter Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed bottom-4 right-4 z-40 bg-[#131c36] text-white p-4 rounded-full shadow-lg hover:bg-[#0f1528] transition-all duration-200 flex items-center gap-2"
        aria-label="Open filters"
      >
        <FaFilter className="text-lg" />
        <span className="text-sm font-semibold">Filters</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar - Mobile Drawer & Desktop Sidebar */}
      <div
        className={`${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 lg:z-auto w-80 lg:w-64 flex-shrink-0 mb-4 sm:mb-6 lg:mb-0 transition-transform duration-300 ease-in-out`}
      >
        <div className="h-full bg-white dark:bg-gray-900 shadow-xl lg:shadow-sm">
          {/* Mobile Close Button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Filters</h3>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="text-gray-500 hover:text-gray-700 p-2"
              aria-label="Close filters"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>
          {sidebarContent}
        </div>
      </div>
    </>
  );
};

export default CatalogueSidebar;
